import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Select, Modal, Table, Badge } from '../components/Common';

export default function SsoSettings() {
  const { apiFetch } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, clientRes, groupRes] = await Promise.all([
        apiFetch('/sso/configs'),
        apiFetch('/clients'),
        apiFetch('/groups')
      ]);
      const configData = await configRes.json().catch(() => null);
      if (!configRes.ok) throw new Error(configData?.error || `Failed to load SSO configurations (${configRes.status})`);
      setConfigs(Array.isArray(configData) ? configData : []);
      setClients(clientRes.ok ? await clientRes.json().catch(() => []) : []);
      setGroups(groupRes.ok ? await groupRes.json().catch(() => []) : []);
    } catch (err) {
      console.error('SSO Settings load error:', err);
      setError(err.message || 'Failed to load SSO settings');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      clientId: '',
      providerName: '',
      issuerUrl: '',
      clientIdValue: '',
      clientSecret: '',
      scopes: 'openid email profile',
      defaultGroupId: '',
      emailDomains: '',
      isActive: true
    });
    setTestResult(null);
    setShowModal(true);
  };

  const openEdit = (config) => {
    setEditing(config.id);
    setForm({
      clientId: config.client_id || '',
      providerName: config.provider_name,
      issuerUrl: config.issuer_url,
      clientIdValue: config.client_id_value,
      clientSecret: '',
      scopes: config.scopes || 'openid email profile',
      defaultGroupId: config.default_group_id || '',
      emailDomains: (config.email_domains || []).join(', '),
      isActive: config.is_active
    });
    setTestResult(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        clientId: form.clientId || null,
        providerName: form.providerName,
        issuerUrl: form.issuerUrl,
        clientIdValue: form.clientIdValue,
        scopes: form.scopes,
        defaultGroupId: form.defaultGroupId || null,
        isActive: form.isActive
      };

      // Parse comma-separated email domains into array
      body.emailDomains = form.emailDomains
        ? form.emailDomains.split(',').map(d => d.trim().toLowerCase()).filter(Boolean)
        : [];

      if (form.clientSecret) {
        body.clientSecret = form.clientSecret;
      }

      let res;
      if (editing) {
        res = await apiFetch(`/sso/configs/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        if (!form.clientSecret) {
          alert('Client secret is required for new configurations');
          setSaving(false);
          return;
        }
        res = await apiFetch('/sso/configs', { method: 'POST', body: JSON.stringify(body) });
      }

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Failed to save');
        setSaving(false);
        return;
      }

      setShowModal(false);
      load();
    } catch (err) {
      alert(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete SSO configuration "${name}"? Users provisioned through this SSO will not be deleted but will no longer be able to sign in via SSO.`)) return;
    try {
      const res = await apiFetch(`/sso/configs/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      load();
    } catch (err) { alert(err.message); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    setTestResult(null);
    try {
      const res = await apiFetch(`/sso/configs/${id}/test`, { method: 'POST' });
      const data = await res.json();
      setTestResult({ id, ...data });
    } catch (err) {
      setTestResult({ id, error: err.message });
    }
    setTesting(null);
  };

  const columns = [
    { key: 'provider_name', label: 'Provider' },
    { key: 'client_name', label: 'Client', render: (v) => v || <Badge>MSP</Badge> },
    { key: 'email_domains', label: 'Email Domains', render: v => {
      const domains = v || [];
      return domains.length > 0
        ? <span style={{ fontSize: 12 }}>{domains.join(', ')}</span>
        : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>;
    }},
    { key: 'issuer_url', label: 'Issuer URL', render: v => (
      <span style={{ fontSize: 12, maxWidth: 250, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
    )},
    { key: 'default_group_name', label: 'Default Group', render: v => v || '—' },
    { key: 'is_active', label: 'Status', render: v => (
      <Badge style={{ background: v ? '#22c55e' : '#6b7280' }}>{v ? 'Active' : 'Inactive'}</Badge>
    )},
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" onClick={() => handleTest(v)}
          style={{ padding: '4px 8px', fontSize: 12 }}
          disabled={testing === v}>
          {testing === v ? 'Testing...' : 'Test'}
        </Button>
        <Button variant="ghost" onClick={() => openEdit(row)}
          style={{ padding: '4px 8px', fontSize: 12 }}>Edit</Button>
        <Button variant="ghost" onClick={() => handleDelete(v, row.provider_name)}
          style={{ padding: '4px 8px', fontSize: 12, color: '#ef4444' }}>Delete</Button>
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>SSO Settings</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>
            Configure OpenID Connect (OIDC) Single Sign-On for clients and MSP
          </p>
        </div>
        <Button onClick={openCreate}>+ Add SSO Provider</Button>
      </div>

      {error && (
        <Card style={{ marginBottom: 16, padding: 16, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <div style={{ color: '#dc2626', fontSize: 14 }}>
            <strong>Error:</strong> {error}
          </div>
        </Card>
      )}

      {loading && !error && (
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading SSO configurations...</p>
        </Card>
      )}

      {testResult && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          {testResult.success ? (
            <div style={{ color: '#22c55e' }}>
              <strong>Connection successful!</strong>
              <div style={{ fontSize: 13, marginTop: 4, color: 'var(--text-secondary)' }}>
                Issuer: {testResult.issuer}
              </div>
            </div>
          ) : (
            <div style={{ color: '#ef4444' }}>
              <strong>Connection failed:</strong> {testResult.error}
            </div>
          )}
        </Card>
      )}

      {!loading && !error && (
        <>
          <Table columns={columns} data={configs} />

          {configs.length === 0 && (
            <Card style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔑</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No SSO Providers Configured</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
                Add an OIDC provider to enable Single Sign-On for your clients or MSP users.
              </p>
              <Button onClick={openCreate}>+ Add SSO Provider</Button>
            </Card>
          )}
        </>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? 'Edit SSO Configuration' : 'Add SSO Provider'} width={640}>

        <Select label="Assign To"
          options={[
            { value: '', label: 'MSP (Organization-wide)' },
            ...clients.map(c => ({ value: c.id, label: c.name }))
          ]}
          value={form.clientId || ''}
          onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}
          disabled={!!editing}
        />

        <Input label="Provider Name *" placeholder="e.g. Okta, Azure AD, Google Workspace"
          value={form.providerName || ''}
          onChange={e => setForm(f => ({ ...f, providerName: e.target.value }))} />

        <Input label="Issuer URL *" placeholder="e.g. https://your-domain.okta.com"
          value={form.issuerUrl || ''}
          onChange={e => setForm(f => ({ ...f, issuerUrl: e.target.value }))} />

        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -8, marginBottom: 12 }}>
          The OIDC issuer URL. The app will discover endpoints via <code>.well-known/openid-configuration</code>
        </div>

        <Input label="Client ID *" placeholder="OIDC Client ID from your identity provider"
          value={form.clientIdValue || ''}
          onChange={e => setForm(f => ({ ...f, clientIdValue: e.target.value }))} />

        <Input label={editing ? "Client Secret (leave empty to keep current)" : "Client Secret *"}
          type="password"
          placeholder="OIDC Client Secret"
          value={form.clientSecret || ''}
          onChange={e => setForm(f => ({ ...f, clientSecret: e.target.value }))} />

        <Input label="Scopes" placeholder="openid email profile"
          value={form.scopes || ''}
          onChange={e => setForm(f => ({ ...f, scopes: e.target.value }))} />

        <Input label="Email Domains" placeholder="e.g. company.com, company.org"
          value={form.emailDomains || ''}
          onChange={e => setForm(f => ({ ...f, emailDomains: e.target.value }))} />

        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -8, marginBottom: 12 }}>
          Comma-separated list of email domains. Users with these domains will be automatically
          redirected to this SSO provider during login.
        </div>

        <Select label="Default Group for New Users"
          options={[
            { value: '', label: 'None (viewer role only)' },
            ...groups.filter(g => g.name !== 'Super Admins').map(g => ({ value: g.id, label: g.name }))
          ]}
          value={form.defaultGroupId || ''}
          onChange={e => setForm(f => ({ ...f, defaultGroupId: e.target.value }))}
        />

        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -8, marginBottom: 12 }}>
          Auto-provisioned users will be assigned to this group on first login
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <input type="checkbox" id="sso-active" checked={form.isActive !== false}
            onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
          <label htmlFor="sso-active" style={{ fontSize: 14, cursor: 'pointer' }}>Active</label>
        </div>

        <Card style={{ padding: 16, marginBottom: 16, background: 'var(--bg-input)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Setup Instructions</div>
          <ol style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
            <li>Create an OIDC application in your identity provider (Okta, Azure AD, Auth0, etc.)</li>
            <li>Set the redirect URI to: <code style={{ background: 'var(--bg)', padding: '2px 6px', borderRadius: 4 }}>
              {window.location.origin}/api/sso/callback
            </code></li>
            <li>Copy the Client ID, Client Secret, and Issuer URL into the fields above</li>
            <li>Ensure the application has <code>openid</code>, <code>email</code>, and <code>profile</code> scopes enabled</li>
          </ol>
        </Card>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.providerName || !form.issuerUrl || !form.clientIdValue}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
